import { ProgramFixedContentRowUpsert } from '../types/domain';
import { mergeWorkspaceRowLearningTextAttachments } from './programWorkspaceDisplayMerge';
import { trimTrailingEmptyProgramContentOverflowRows } from './programWorkspaceBaselineDigest';
import {
  ensureProgramWorkspaceHydrated,
  flushProgramWorkspaceRepository,
  getProgramWorkspaceMemoryState,
  markProgramWorkspaceClipboardDirty,
  registerProgramWorkspaceRepositoryPageHideFlush,
  replaceProgramWorkspaceMemoryState,
  scheduleProgramWorkspaceScopePersist,
} from './programWorkspaceRepository';
import { rowStableKey } from './programWorkspaceIdb';

export type {
  ProgramWorkspaceClipboard,
  ProgramWorkspaceClipboardCell,
  ProgramWorkspacePersistKind,
  ProgramWorkspaceRowsChangeMeta,
  ProgramWorkspaceScopeEntry,
  ProgramWorkspaceScopeType,
} from './programWorkspaceTypes';

export {
  resolveProgramWorkspacePersistKind,
} from './programWorkspaceTypes';

export const PROGRAM_WORKSPACE_CHANGED_EVENT = 'cp-program-workspace-changed';

function emitWorkspaceChanged(detail?: { scopeId?: string; clearedCellIds?: string[] }): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PROGRAM_WORKSPACE_CHANGED_EVENT, { detail }));
}

function loadState() {
  return getProgramWorkspaceMemoryState();
}

function saveState(
  next: ReturnType<typeof loadState>,
  options?: { skipEvent?: boolean; changedScopeId?: string }
): void {
  replaceProgramWorkspaceMemoryState(next);
  if (options?.changedScopeId) {
    scheduleProgramWorkspaceScopePersist(options.changedScopeId);
  } else {
    Object.keys(next.scopes).forEach((scopeId) => scheduleProgramWorkspaceScopePersist(scopeId));
    markProgramWorkspaceClipboardDirty();
  }
  if (!options?.skipEvent) {
    emitWorkspaceChanged();
  }
}

/** 앱 부트스트랩 — index/App에서 1회 호출 */
export function initProgramWorkspaceStorage(): Promise<void> {
  return ensureProgramWorkspaceHydrated();
}

export { flushProgramWorkspaceRepository, registerProgramWorkspaceRepositoryPageHideFlush };

export function getProgramWorkspaceScopeEntry(scopeId: string): import('./programWorkspaceTypes').ProgramWorkspaceScopeEntry | null {
  return loadState().scopes[scopeId] ?? null;
}

export function readProgramWorkspaceLearningTextByAnnualCellKey(
  scopeId: string,
  annualCellKey: string
): string | null {
  const entry = getProgramWorkspaceScopeEntry(scopeId.trim());
  if (!entry?.isDirty) return null;
  const ak = annualCellKey.trim();
  if (!ak) return null;
  for (const row of entry.rows) {
    if ((row.source_annual_cell_key ?? '').trim() !== ak) continue;
    if (typeof row.learning_text === 'string') return row.learning_text;
  }
  return null;
}

export function readProgramWorkspaceRowPatchForAnnualCell(params: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  annualCellKey: string;
}): ProgramFixedContentRowUpsert | null {
  const sid = params.workspaceId.trim();
  const gid = params.gradeId.trim();
  const ak = params.annualCellKey.trim();
  if (!sid || !gid || !ak) return null;
  for (const entry of getProgramWorkspaceScopeEntries()) {
    if (!entry.isDirty) continue;
    if (entry.workspaceId.trim() !== sid) continue;
    if (entry.gradeId.trim() !== gid) continue;
    if (entry.academicYear !== params.academicYear || entry.termNumber !== params.termNumber) continue;
    const hit = entry.rows.find((row) => (row.source_annual_cell_key ?? '').trim() === ak);
    if (hit) return hit;
  }
  return null;
}

export function readProgramWorkspaceLearningTextForAnnualCell(params: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  annualCellKey: string;
}): string | null {
  const patch = readProgramWorkspaceRowPatchForAnnualCell(params);
  if (!patch || typeof patch.learning_text !== 'string') return null;
  return patch.learning_text;
}

export function getProgramWorkspaceScopeEntries(): import('./programWorkspaceTypes').ProgramWorkspaceScopeEntry[] {
  return Object.values(loadState().scopes);
}

function normalizeScopeEntryForCompare(entry: import('./programWorkspaceTypes').ProgramWorkspaceScopeEntry) {
  return {
    scopeId: entry.scopeId,
    scopeType: entry.scopeType,
    scopeKey: entry.scopeKey,
    workspaceId: entry.workspaceId,
    gradeId: entry.gradeId,
    academicYear: entry.academicYear,
    termNumber: entry.termNumber,
    rows: entry.rows,
    isDirty: entry.isDirty,
    persistKind: entry.persistKind,
    dirtyRowKeys: entry.dirtyRowKeys,
    deletedRowIds: entry.deletedRowIds,
    baselineRevision: entry.baselineRevision,
    trackSyncFanoutScopeKeys: entry.trackSyncFanoutScopeKeys,
    trackSyncScheduleAnnualKeysByScope: entry.trackSyncScheduleAnnualKeysByScope,
  };
}

function mergeWorkspaceRowsForPatch(
  prevRows: ProgramFixedContentRowUpsert[],
  patchRows: ProgramFixedContentRowUpsert[]
): ProgramFixedContentRowUpsert[] {
  if (!prevRows.length) return patchRows.map((row) => ({ ...row }));
  if (!patchRows.length) return prevRows.map((row) => ({ ...row }));

  const patchById = new Map<string, ProgramFixedContentRowUpsert>();
  const patchBySourceAnnualKey = new Map<string, ProgramFixedContentRowUpsert>();
  const patchByOrderIndex = new Map<number, ProgramFixedContentRowUpsert>();
  const consumed = new Set<ProgramFixedContentRowUpsert>();

  patchRows.forEach((row) => {
    const id = (row.id ?? '').trim();
    if (id) patchById.set(id, row);
    const annualKey = (row.source_annual_cell_key ?? '').trim();
    if (annualKey) patchBySourceAnnualKey.set(annualKey, row);
    patchByOrderIndex.set(row.order_index, row);
  });

  const merged = prevRows.map((prevRow) => {
    const prevId = (prevRow.id ?? '').trim();
    const prevAnnualKey = (prevRow.source_annual_cell_key ?? '').trim();
    const hit =
      (prevId ? patchById.get(prevId) : undefined) ??
      (prevAnnualKey ? patchBySourceAnnualKey.get(prevAnnualKey) : undefined) ??
      patchByOrderIndex.get(prevRow.order_index);
    if (!hit) return { ...prevRow };
    consumed.add(hit);
    return mergeWorkspaceRowLearningTextAttachments(prevRow, hit);
  });

  patchRows.forEach((row) => {
    if (consumed.has(row)) return;
    merged.push({ ...row });
  });

  return merged;
}

function collectDirtyRowKeys(rows: ProgramFixedContentRowUpsert[]): string[] {
  const keys = new Set<string>();
  rows.forEach((row, index) => keys.add(rowStableKey(row, index)));
  return Array.from(keys);
}

export function upsertProgramWorkspaceScopeEntry(entry: import('./programWorkspaceTypes').ProgramWorkspaceScopeEntry): void {
  const state = loadState();
  const prev = state.scopes[entry.scopeId];
  const requestedKind = entry.persistKind ?? 'patch';
  /**
   * 행 삭제·재배치 등 `replace` 스냅샷이 dirty인 동안 셀 편집 `patch`가 오면
   * persistKind를 patch로 내리면 display merge가 baseline 길이를 되살린다.
   * → replace를 유지한 채 prev 스냅샷에 patch만 병합한다.
   */
  const persistKind =
    prev?.isDirty && prev.persistKind === 'replace' && requestedKind === 'patch'
      ? 'replace'
      : requestedKind;

  let mergedRows: ProgramFixedContentRowUpsert[];
  if (requestedKind === 'patch' && prev?.rows?.length) {
    mergedRows = mergeWorkspaceRowsForPatch(prev.rows, entry.rows);
  } else {
    mergedRows = entry.rows.map((row) => ({ ...row }));
  }

  const normalizedEntry: import('./programWorkspaceTypes').ProgramWorkspaceScopeEntry = {
    ...entry,
    rows: mergedRows,
    persistKind,
    dirtyRowKeys:
      persistKind === 'patch'
        ? Array.from(new Set([...(prev?.dirtyRowKeys ?? []), ...collectDirtyRowKeys(entry.rows)]))
        : undefined,
    deletedRowIds: persistKind === 'replace' ? entry.deletedRowIds ?? prev?.deletedRowIds : prev?.deletedRowIds,
  };

  const nextComparable = JSON.stringify(normalizeScopeEntryForCompare(normalizedEntry));
  const prevComparable = prev ? JSON.stringify(normalizeScopeEntryForCompare(prev)) : null;
  if (prevComparable === nextComparable) return;

  state.scopes[entry.scopeId] = { ...normalizedEntry, updatedAt: Date.now() };
  saveState(state, { changedScopeId: entry.scopeId });
}

export function ensureProgramWorkspaceScopeSnapshot(entry: import('./programWorkspaceTypes').ProgramWorkspaceScopeEntry): void {
  const state = loadState();
  const prev = state.scopes[entry.scopeId];
  if (prev) return;
  state.scopes[entry.scopeId] = { ...entry, isDirty: false, updatedAt: Date.now() };
  saveState(state, { changedScopeId: entry.scopeId });
}

export function removeProgramWorkspaceScopeEntry(scopeId: string): void {
  const state = loadState();
  if (!state.scopes[scopeId]) return;
  delete state.scopes[scopeId];
  saveState(state, { changedScopeId: scopeId });
}

export function discardProgramWorkspaceScopesForAnnualTimetableChange(params: {
  workspaceId: string;
  academicYear: number;
  termNumbers: Array<1 | 2>;
  gradeIds: string[];
}): void {
  const { workspaceId, academicYear, termNumbers, gradeIds } = params;
  if (!workspaceId || typeof window === 'undefined') return;
  const termSet = new Set(termNumbers.filter((value): value is 1 | 2 => value === 1 || value === 2));
  const gradeSet = new Set(gradeIds.map((value) => value.trim()).filter(Boolean));
  if (termSet.size === 0 || gradeSet.size === 0) return;

  const state = loadState();
  const removedScopeIds = new Set<string>();
  let changed = false;

  Object.entries(state.scopes).forEach(([scopeId, entry]) => {
    if (
      entry.workspaceId !== workspaceId ||
      entry.academicYear !== academicYear ||
      !termSet.has(entry.termNumber) ||
      !gradeSet.has(entry.gradeId)
    ) {
      return;
    }
    /** 저장 안 한 편집본은 지우지 않는다(학습내용은 위치로 짝지으므로 시간표가 바뀌어도 그대로 유효) */
    if (entry.isDirty) return;
    delete state.scopes[scopeId];
    removedScopeIds.add(scopeId);
    changed = true;
  });

  if (state.clipboard?.isCut && removedScopeIds.has(state.clipboard.sourceScopeId)) {
    state.clipboard = null;
    changed = true;
  }

  if (changed) saveState(state);
}

export function registerDiscardDirtyWorkspaceOnPageHide(scopeId: string): () => void {
  if (typeof window === 'undefined' || !scopeId) return () => {};
  const onPageHide = () => {
    const ent = getProgramWorkspaceScopeEntry(scopeId);
    if (ent?.isDirty) removeProgramWorkspaceScopeEntry(scopeId);
    void flushProgramWorkspaceRepository();
  };
  window.addEventListener('pagehide', onPageHide);
  return () => window.removeEventListener('pagehide', onPageHide);
}

function collectRetainedScopeIds(
  state: ReturnType<typeof loadState>,
  retainScopeIds: string[]
): Set<string> {
  const retained = new Set(retainScopeIds.filter(Boolean));
  const cutSourceScopeId = state.clipboard?.isCut ? state.clipboard.sourceScopeId?.trim() ?? '' : '';
  if (cutSourceScopeId) retained.add(cutSourceScopeId);
  Object.values(state.scopes).forEach((entry) => {
    if (entry.isDirty) retained.add(entry.scopeId);
  });
  return retained;
}

export function pruneProgramWorkspaceScopes(retainScopeIds: string[] = []): void {
  const state = loadState();
  const retained = collectRetainedScopeIds(state, retainScopeIds);
  let changed = false;
  Object.keys(state.scopes).forEach((scopeId) => {
    if (retained.has(scopeId)) return;
    delete state.scopes[scopeId];
    changed = true;
  });
  if (changed) saveState(state);
}

export function getProgramWorkspaceDirtyCount(): number {
  return Object.values(loadState().scopes).filter((entry) => !!entry.isDirty).length;
}

export function discardAllDirtyProgramWorkspaceScopes(): void {
  const state = loadState();
  let changed = false;
  Object.entries(state.scopes).forEach(([scopeId, entry]) => {
    if (!entry?.isDirty) return;
    delete state.scopes[scopeId];
    changed = true;
  });
  if (changed) saveState(state);
}

export function isProgramWorkspaceScopeDirty(scopeId: string): boolean {
  if (!scopeId.trim()) return false;
  const entry = getProgramWorkspaceScopeEntry(scopeId);
  return Boolean(entry?.isDirty);
}

export function isAnyProgramWorkspaceScopeDirty(scopeIds: readonly string[]): boolean {
  return scopeIds.some((id) => isProgramWorkspaceScopeDirty(id));
}

export function loadProgramWorkspaceClipboard(): import('./programWorkspaceTypes').ProgramWorkspaceClipboard | null {
  return loadState().clipboard ?? null;
}

export function saveProgramWorkspaceClipboard(
  clipboard: import('./programWorkspaceTypes').ProgramWorkspaceClipboard | null
): void {
  const state = loadState();
  state.clipboard = clipboard;
  saveState(state);
  markProgramWorkspaceClipboardDirty();
}

function mapFieldToRowKey(fieldId: string): keyof ProgramFixedContentRowUpsert | null {
  if (fieldId === 'unit_text') return 'unit_text';
  if (fieldId === 'learning_text') return 'learning_text';
  if (fieldId === 'page_text') return 'page_text';
  if (fieldId === 'assistant_page_text') return 'assistant_page_text';
  if (fieldId === 'current_period_text') return 'current_period_text';
  if (fieldId === 'total_period_text') return 'total_period_text';
  if (fieldId === 'material_text') return 'material_text';
  if (fieldId === 'record_cross_curricular_text') return 'record_cross_curricular_text';
  if (fieldId === 'record_memo_text') return 'record_memo_text';
  if (fieldId === 'record_evaluation_text') return 'record_evaluation_text';
  if (fieldId === 'record_class_text') return 'record_class_text';
  return null;
}

export function applyCrossScopeCutClearToWorkspace(sourceScopeId: string, sourceCellIds: string[]): void {
  if (!sourceScopeId || sourceCellIds.length === 0) return;
  const state = loadState();
  const source = state.scopes[sourceScopeId];
  if (!source) return;
  const nextRows = source.rows.map((row) => ({ ...row }));
  let changed = false;
  const resolveRowByAnnualKey = (annualKey: string) => {
    const bySourceAnnual = nextRows.find((row) => (row.source_annual_cell_key ?? '') === annualKey);
    if (bySourceAnnual) return bySourceAnnual;
    if (annualKey.startsWith('extra:')) {
      const idx = Number(annualKey.slice('extra:'.length));
      if (Number.isInteger(idx) && idx >= 0 && idx < nextRows.length) return nextRows[idx];
    }
    if (annualKey.startsWith('orphan:')) {
      const token = annualKey.slice('orphan:'.length);
      if (token) {
        const byId = nextRows.find((row) => (row.id ?? '') === token);
        if (byId) return byId;
      }
      const idx = Number(token);
      if (Number.isInteger(idx) && idx >= 0 && idx < nextRows.length) return nextRows[idx];
    }
    return null;
  };
  const clearedCellIds: string[] = [];
  sourceCellIds.forEach((cellId) => {
    const sep = cellId.includes('::') ? '::' : '\t';
    const [annualKey, fieldId] = cellId.split(sep);
    if (!annualKey || !fieldId) return;
    const rowKey = mapFieldToRowKey(fieldId);
    if (!rowKey) return;
    const target = resolveRowByAnnualKey(annualKey);
    if (!target) return;
    if ((target[rowKey] ?? '') === '') return;
    (target[rowKey] as string | null) = null;
    clearedCellIds.push(cellId);
    changed = true;
  });
  if (!changed) return;
  state.scopes[sourceScopeId] = {
    ...source,
    rows: nextRows,
    isDirty: true,
    updatedAt: Date.now(),
    persistKind: 'patch',
    dirtyRowKeys: Array.from(new Set([...(source.dirtyRowKeys ?? []), ...collectDirtyRowKeys(nextRows)])),
  };
  saveState(state, { skipEvent: true, changedScopeId: sourceScopeId });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(PROGRAM_WORKSPACE_CHANGED_EVENT, {
        detail: { scopeId: sourceScopeId, clearedCellIds },
      })
    );
  }
}

/** 프로그램 워크스페이스(LocalStorage·IndexedDB) 완전 초기화 */
export async function clearProgramWorkspaceStorageCompletely(): Promise<void> {
  replaceProgramWorkspaceMemoryState({ scopes: {}, clipboard: null });
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('cp:program-workspace:v1');
  }
  const { idbClearAllProgramWorkspace } = await import('./programWorkspaceIdb');
  await idbClearAllProgramWorkspace();
  emitWorkspaceChanged();
}

export function setProgramWorkspaceScopeBaselineRevision(scopeId: string, revision: string): void {
  const sid = scopeId.trim();
  const rev = revision.trim();
  if (!sid || !rev) return;
  const state = loadState();
  const entry = state.scopes[sid];
  if (!entry || entry.baselineRevision === rev) return;
  state.scopes[sid] = { ...entry, baselineRevision: rev, updatedAt: Date.now() };
  saveState(state, { changedScopeId: sid, skipEvent: true });
}

/** patch 저장 시 서버에 보낼 변경 행만 추출 */
export function pickProgramWorkspacePatchRowsForSave(
  entry: import('./programWorkspaceTypes').ProgramWorkspaceScopeEntry
): ProgramFixedContentRowUpsert[] {
  if (entry.persistKind === 'replace') {
    return trimTrailingEmptyProgramContentOverflowRows(entry.rows);
  }
  const dirtyKeys = new Set(entry.dirtyRowKeys ?? []);
  if (!dirtyKeys.size) return entry.rows;
  return entry.rows.filter((row, index) => dirtyKeys.has(rowStableKey(row, index)));
}
